/**
 * Handing a finished clip — or the sheet's own link — to WhatsApp, Instagram, Facebook
 * and the rest, from the WEBSITE.
 *
 * The installed app does this with a native plugin (mobile/nativeMedia.js). The website has
 * only the browser, and the browser offers exactly one route that carries a real file:
 * `navigator.share({ files })`, the phone's own share sheet. There is no per-platform
 * alternative to fall back on — Facebook's share link takes a URL, WhatsApp's takes text,
 * and Instagram publishes no web share link at all. So this is one button and one sheet,
 * never three logos.
 *
 * Two platform rules shape every caller here, and both are load-bearing:
 *
 *  1. A file share needs a secure context and a browser that admits files. Desktop browsers
 *     mostly refuse, so `canShareFiles()` is false there and the caller simply keeps
 *     downloading — sharing is strictly additive and never replaces a working path.
 *
 *  2. A share must begin inside a *transient user activation* — roughly five seconds after a
 *     tap, in WebKit. Trimming a clip on the server can outrun that window, so a caller must
 *     never `await` the file and then share. It shows the user a button once the blob is in
 *     hand and shares on THAT tap, which starts a fresh window.
 */

/** Where a shared clip points people back to. NOT the app's `cherryclipz.com`, which currently redirects in a loop. */
export const SHARE_SITE = "app.cherryclipz.com";

/**
 * Is this the same bundle running inside the Capacitor shell?
 *
 * Detected here rather than imported: the phone app lives on its own branch, so
 * `components/mobile/native.js` is not present on every branch this file has to build on.
 * The native runtime injects `window.Capacitor` before our code runs, so the check is the
 * same one `native.js` makes.
 */
function inNativeShell() {
  try {
    const cap = typeof window !== "undefined" && window.Capacitor;
    return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
  } catch (e) {
    return false;
  }
}

let filesProbe = null;

/** Test seam: forget the cached capability probe. */
export function _resetShareProbe() {
  filesProbe = null;
}

/**
 * Can this browser hand a video FILE to another app?
 *
 * False on desktop, false without HTTPS, and false inside the Capacitor shell — the installed
 * app has its own native sheet, and the Android WebView omits the Web Share API entirely
 * (which is why `shareText` exists on the native plugin at all).
 */
export function canShareFiles() {
  if (filesProbe !== null) return filesProbe;
  filesProbe = false;
  try {
    if (typeof navigator === "undefined" || typeof navigator.share !== "function") return filesProbe;
    if (typeof navigator.canShare !== "function") return filesProbe;
    if (inNativeShell()) return filesProbe;
    const probe = new File([new Uint8Array(1)], "probe.mp4", { type: "video/mp4" });
    filesProbe = !!navigator.canShare({ files: [probe] });
  } catch (e) {
    filesProbe = false;
  }
  return filesProbe;
}

/** Can this browser share a plain link? Far more widely true than files — phones and some desktops. */
export function canShareLink() {
  try {
    return typeof navigator !== "undefined" && typeof navigator.share === "function" && !inNativeShell();
  } catch (e) {
    return false;
  }
}

/** The caption that travels with a clip. Some targets keep it, some drop it; Instagram ignores it. */
export function clipShareText(sentence) {
  const line = String(sentence || "").trim().replace(/\s+/g, " ");
  const trimmed = line.length > 120 ? `${line.slice(0, 119).trimEnd()}…` : line;
  return trimmed ? `${trimmed}\n\n🍒 ${SHARE_SITE}` : `🍒 ${SHARE_SITE}`;
}

/**
 * Open the phone's share sheet on a finished clip.
 *
 * MUST be called straight from a click handler — see rule 2 above. Deliberately passes no
 * `url` alongside `files`: combining the two makes iOS refuse the whole share.
 *
 * @returns {Promise<'shared'|'cancelled'|'unsupported'|'error'>}
 */
export async function shareVideoFile({ blob, filename, text }) {
  if (!canShareFiles()) return "unsupported";
  let file;
  try {
    file = new File([blob], filename || "clip.mp4", { type: blob.type || "video/mp4" });
  } catch (e) {
    return "error";
  }
  // Re-ask with the REAL file: a browser may accept a 1-byte probe and refuse a 40MB video.
  try {
    if (!navigator.canShare({ files: [file] })) return "unsupported";
  } catch (e) {
    return "unsupported";
  }
  try {
    await navigator.share({ files: [file], title: "CherryClipz", text: text || "" });
    return "shared";
  } catch (e) {
    // Swiping the sheet away is a normal outcome, not a failure to report.
    if (e && (e.name === "AbortError" || e.name === "NotAllowedError")) {
      return e.name === "AbortError" ? "cancelled" : "unsupported";
    }
    return "error";
  }
}

/**
 * Share a link — the sheet's own `/s/` URL. Falls back to the clipboard wherever the share
 * sheet isn't available, so the button always does something the user can see.
 *
 * @returns {Promise<'shared'|'cancelled'|'copied'|'error'>}
 */
export async function shareLink({ url, title, text }) {
  if (canShareLink()) {
    try {
      await navigator.share({ url, title: title || "CherryClipz", text: text || "" });
      return "shared";
    } catch (e) {
      if (e && e.name === "AbortError") return "cancelled";
      // Anything else — a permissions-policy block in an iframe, say — falls through to copying.
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch (e) {
    return "error";
  }
}

/** A filesystem-safe name for a clip, from its own words. Mirrors the download naming. */
export function clipFileName(sentence, ext = "mp4") {
  const safe = String(sentence || "")
    .substring(0, 50)
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .trim()
    .replace(/ +/g, "_");
  return `${safe || "clip"}.${ext}`;
}
